import { useState, useEffect } from "react";
import { Icon, CatIcon } from "../shared/Icons";
import { DarkToggle } from "../shared/DarkToggle";
import { StatusBadge } from "../shared/StatusBadge";
import { Toast } from "../shared/Toast";
import { genOrderId } from "../../data/orders";
import { UpiPaymentModal } from "./UpiPaymentModal";

export function UserPortal({ user, products, loading, onLogout, onPlaceOrder, userOrders = [], dark, setDark }) {
  const [view, setView] = useState("shop");
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [cart, setCart] = useState([]);
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kd_likes')) || []; }
    catch { return []; }
  });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [toast, setToast] = useState("");
  const [lastOrder, setLastOrder] = useState(null);
  const [payment, setPayment] = useState("UPI");
  const [form, setForm] = useState({ name: user.name, address: "", city: "", state: "", pin: "", contact: "", regNumber: "", notes: "" });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUpiModal, setShowUpiModal] = useState(false);

  const closeMobileNav = (nextView) => { navigateTo(nextView); setMobileMenuOpen(false); };

  // ─────────────────────────────────────────────────────────────
  //  HISTORY NAVIGATION (Back Button Support)
  // ─────────────────────────────────────────────────────────────
  const navigateTo = (nextView, data = null) => {
    // Prevent redundant history entries
    if (nextView === view && (!data || data.id === selected?.id)) return;

    const state = { view: nextView, selectedId: data?.id || null };
    window.history.pushState(state, "");
    
    if (data) setSelected(data);
    setView(nextView);
  };

  useEffect(() => {
    const handlePopState = (e) => {
      const state = e.state;
      if (state && state.view) {
        if (state.view === "detail" && state.selectedId) {
          const p = products.find(prod => String(prod.id) === String(state.selectedId));
          if (p) setSelected(p);
        }
        setView(state.view);
      } else {
        setView("shop"); // Default fallback
      }
    };

    window.addEventListener("popstate", handlePopState);
    
    // Initialize root state if not exists
    if (!window.history.state) {
      window.history.replaceState({ view: "shop" }, "");
    }

    return () => window.removeEventListener("popstate", handlePopState);
  }, [products]);

  useEffect(() => {
    localStorage.setItem('kd_likes', JSON.stringify(likes));
  }, [likes]);

  const toggleLike = (product) => {
    setLikes(prev => {
      const isLiked = prev.some(p => p.id === product.id);
      if (isLiked) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 3100); };
  const cats = ["All", ...new Set(products.map(p => p.category))];

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchName = p.name ? p.name.toLowerCase().includes(q) : false;
    const matchDesc = p.description ? p.description.toLowerCase().includes(q) : false;
    return (category === "All" || p.category === category) && (!q || matchName || matchDesc);
  });
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const addToCart = (product, q = 1) => {
    setCart(c => {
      const ex = c.find(i => i.id === product.id);
      return ex ? c.map(i => i.id === product.id ? { ...i, qty: i.qty + q } : i) : [...c, { ...product, qty: q }];
    });
    showToast(`${product.name} added to cart`);
  };
  const removeFromCart = id => setCart(c => c.filter(i => i.id !== id));
  const updCartQty = (id, delta) => setCart(c => c.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const apiHelper = async (url, options = {}) => {
    const token = localStorage.getItem("kd_token");
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        ...options.headers
      }
    });
    if (!res.ok) {
      try {
        const errorData = await res.json();
        throw errorData;
      } catch (e) {
        if (e.error) throw e;
        throw new Error(`Server returned ${res.status}`);
      }
    }
    return res.json();
  };

  const placeOrder = async () => {
    const orderData = {
      items: cart.map(i => ({ productId: i.id, name: i.name, qty: i.qty, price: i.price })),
      total: cartTotal,
      address: `${form.address}, ${form.city}, ${form.state} – ${form.pin}`,
      contact: form.contact, payment, notes: form.notes, regNumber: form.regNumber,
    };

    if (payment === "UPI") {
      if (!cartTotal || cartTotal === 0) return showToast("Cart is empty");
      setShowUpiModal(true);
      return;
    }


    try {
      const order = await onPlaceOrder(orderData);
      setLastOrder(order);
      setCart([]);
      navigateTo("confirm");
    } catch {
      showToast("Failed to place order. Please try again.");
    }
  };

  const payMethods = [
    { id: "UPI", icon: "upi", label: "UPI Payment", desc: "Pay via any UPI app manually" },
    { id: "Cash on Delivery", icon: "cash", label: "Cash on Delivery", desc: "Pay when your order arrives" },

  ];

  return (
    <div className="user-app page-enter">
      {/* NAV */}
      <nav className="user-nav">
        {/* LEFT: Logo + desktop nav links */}
        <div className="user-nav-left">
          <div className="nav-logo" onClick={() => closeMobileNav("shop")}>
            <div className="logo-mark"><img src="/logo.png" alt="K Dental" /></div>
            <span className="logo-text" style={{ fontSize: 18 }}>K <span>Dental</span></span>
          </div>
          <div className="user-nav-links">
            <button className={`nav-link ${view === "shop" ? "active" : ""}`} onClick={() => navigateTo("shop")}>Shop</button>
            <button className={`nav-link ${view === "liked" ? "active" : ""}`} onClick={() => navigateTo("liked")}>Liked</button>
            <button className={`nav-link ${view === "orders" ? "active" : ""}`} onClick={() => navigateTo("orders")}>My Orders</button>
          </div>
        </div>

        {/* RIGHT: Desktop items */}
        <div className="user-nav-right">
          <span className="nav-user">Hi, {user.name.split(" ")[0]}</span>
          <DarkToggle dark={dark} setDark={setDark} />
          <button className="cart-btn" onClick={() => navigateTo("cart")}>
            <Icon name="cart" size={14} />
            <span className="cart-label">Cart</span>
            {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
          </button>
          <button className="btn btn-ghost btn-sm nav-signout" onClick={onLogout}>Sign Out</button>
        </div>

        {/* MOBILE RIGHT: Only cart icon + hamburger */}
        <div className="user-nav-mobile">
          <button className="cart-icon-btn" onClick={() => closeMobileNav("cart")} aria-label="Cart">
            <Icon name="cart" size={18} />
            {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
          </button>
          <button className="hamburger-btn" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Menu">
            <span style={{ transform: mobileMenuOpen ? "rotate(45deg) translate(4px,4px)" : "none" }} />
            <span style={{ opacity: mobileMenuOpen ? 0 : 1 }} />
            <span style={{ transform: mobileMenuOpen ? "rotate(-45deg) translate(4px,-4px)" : "none" }} />
          </button>
        </div>

        {/* MOBILE DROPDOWN DRAWER */}
        {mobileMenuOpen && (
          <div className="mobile-nav-drawer open">
            <div className="drawer-user-row">
              <div className="drawer-avatar">{user.name[0]}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text1)" }}>{user.name}</div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>Buyer Account</div>
              </div>
            </div>
            <div className="mobile-nav-drawer-divider" />
            <button className={`drawer-nav-item ${view === "shop" ? "active" : ""}`} onClick={() => closeMobileNav("shop")}>
              <Icon name="products" size={15} /> Shop
            </button>
            <button className={`drawer-nav-item ${view === "liked" ? "active" : ""}`} onClick={() => closeMobileNav("liked")}>
              <Icon name="heart" size={15} /> Liked
            </button>
            <button className={`drawer-nav-item ${view === "orders" ? "active" : ""}`} onClick={() => closeMobileNav("orders")}>
              <Icon name="orders" size={15} /> My Orders
            </button>
            <button className={`drawer-nav-item ${view === "cart" ? "active" : ""}`} onClick={() => closeMobileNav("cart")}>
              <Icon name="cart" size={15} /> Cart {cartCount > 0 && <span className="drawer-badge">{cartCount}</span>}
            </button>
            <div className="mobile-nav-drawer-divider" />
            <div style={{ padding: "10px 16px 4px" }}>
              <DarkToggle dark={dark} setDark={setDark} />
            </div>
            <button className="drawer-nav-item drawer-signout" onClick={() => { setMobileMenuOpen(false); onLogout(); }}>
              <Icon name="logout" size={15} /> Sign Out
            </button>
          </div>
        )}
      </nav>

      <div className="user-content">

        {/* SHOP VIEW */}
        {view === "shop" && <>
          <div className="user-hero">
            <h2>Premium Dental Supplies</h2>
            <p>Trusted equipment &amp; medication for professionals across India</p>
            <div className="user-search">
              <input placeholder="Search products, categories…" value={search} onChange={e => setSearch(e.target.value)} />
              <button className="user-search-btn"><Icon name="search" size={14} /> Search</button>
            </div>
          </div>
          <div className="user-main">
            <div className="filter-chips">
              {cats.map(c => <button key={c} className={`chip ${category === c ? "active" : ""}`} onClick={() => setCategory(c)}>{c}</button>)}
            </div>
            <div className="section-header">
              <div className="section-title">{category === "All" ? "All Products" : category}</div>
              <span className="section-count">{filtered.length} products</span>
            </div>
            <div className="section-sub" style={{ marginBottom: 16 }}>Browse our complete catalogue</div>
            {loading && products.length === 0 ? (
              <div className="products-user-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="product-user-card skeleton-card">
                    <div className="product-user-img skeleton-img" />
                    <div className="product-user-info" style={{ padding: 16 }}>
                      <div className="skeleton-line" style={{ width: '40%', height: 12, marginBottom: 8 }} />
                      <div className="skeleton-line" style={{ width: '80%', height: 16, marginBottom: 12 }} />
                      <div className="skeleton-line" style={{ width: '30%', height: 14 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="search" size={44} /></div>
                <h3>No Products Found</h3><p>Try a different search or category</p>
              </div>
            ) : (
              <div className="products-user-grid">
                {filtered.map((p, i) => (
                  <div className="product-user-card" key={p.id} onClick={() => { setQty(1); navigateTo("detail", p); }}>
                    <div className="product-user-img">
                      {p.image
                        ? <img src={p.image} alt={p.name} className="product-img-photo" style={{ opacity: 1 }} />
                        : <CatIcon category={p.category} size={44} />
                      }
                      {p.badge && (
                        <div className={`product-card-badge ${p.badge === "Best Seller" ? "badge-best" : p.badge === "New" ? "badge-new" : "badge-sale"}`}>
                          {p.badge}
                        </div>
                      )}
                    </div>
                    <div className="product-user-info">
                      <div className="cat-tag">{p.category}</div>
                      <div className="p-name">{p.name}</div>
                      <div className="p-desc">{p.description}</div>
                      <div className="price-row">
                        <div className="p-price">₹{p.price.toLocaleString()}<sub>/unit</sub></div>
                        <button className="add-cart-btn" onClick={e => { e.stopPropagation(); addToCart(p); }}>
                          <Icon name="plus" size={12} /> Cart
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>}

        {/* LIKED VIEW */}
        {view === "liked" && <>
          <div className="user-main">
            <div className="section-header" style={{ marginBottom: 22 }}>
              <div className="section-title">Liked Products</div>
              <span className="section-count">{likes.length} products</span>
            </div>
            {likes.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="heart" size={44} /></div>
                <h3>No Liked Products</h3><p>You haven't liked any products yet.</p>
                <button className="btn btn-primary" onClick={() => navigateTo("shop")}>Browse Products</button>
              </div>
            ) : (
              <div className="products-user-grid">
                {likes.map(p => (
                  <div className="product-user-card" key={p.id} onClick={() => { setQty(1); navigateTo("detail", p); }}>
                    <div className="product-user-img">
                      {p.image
                        ? <img src={p.image} alt={p.name} className="product-img-photo" style={{ opacity: 1 }} />
                        : <CatIcon category={p.category} size={44} />
                      }
                      {p.badge && (
                        <div className={`product-card-badge ${p.badge === "Best Seller" ? "badge-best" : p.badge === "New" ? "badge-new" : "badge-sale"}`}>
                          {p.badge}
                        </div>
                      )}
                    </div>
                    <div className="product-user-info">
                      <div className="cat-tag">{p.category}</div>
                      <div className="p-name">{p.name}</div>
                      <div className="p-desc">{p.description}</div>
                      <div className="price-row">
                        <div className="p-price">₹{p.price.toLocaleString()}<sub>/unit</sub></div>
                        <button className="add-cart-btn" onClick={e => { e.stopPropagation(); addToCart(p); }}>
                          <Icon name="plus" size={12} /> Cart
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>}

        {/* DETAIL VIEW */}
        {view === "detail" && selected && (
          <div className="product-detail page-enter">
            <button className="back-btn" onClick={() => navigateTo("shop")}><Icon name="arrowLeft" size={14} /> Back to Products</button>
            <div className="detail-grid">
              <div className="detail-img">
                {selected.badge && <div className="detail-badge-top">{selected.badge}</div>}
                <button className={`detail-fav-btn ${likes.some(p => p.id === selected.id) ? "liked" : ""}`} onClick={() => toggleLike(selected)}>
                  <Icon name={likes.some(p => p.id === selected.id) ? "heartFilled" : "heart"} size={18} />
                </button>
                {selected.image
                  ? <img src={selected.image} alt={selected.name} />
                  : <CatIcon category={selected.category} size={80} />
                }
              </div>
              <div className="detail-info">
                <div className="detail-cat-row">
                  <span className="cat-tag">{selected.category}</span>
                </div>
                <h2>{selected.name}</h2>
                <div className="detail-price">
                  ₹{selected.price.toLocaleString()}
                  <span className="detail-price-old">₹{Math.floor(selected.price * 1.2).toLocaleString()}</span>
                </div>
                <p className="detail-desc">{selected.description}</p>
                
                <div className="detail-options-row">
                  <div className="detail-stock"><Icon name="check" size={13} /> {selected.qty} units in stock</div>
                  <div className="qty-control">
                    <label>Quantity:</label>
                    <div className="qty-btns">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}><Icon name="minus" size={14} /></button>
                      <span>{qty}</span>
                      <button onClick={() => setQty(q => Math.min(selected.qty, q + 1))}><Icon name="plus" size={14} /></button>
                    </div>
                  </div>
                </div>

                <div className="detail-actions">
                  <button className="btn-add-cart" onClick={() => { addToCart(selected, qty); navigateTo("shop"); }}>
                    <Icon name="cart" size={15} /> Add to Cart
                  </button>
                  <button className="btn-buy-now" onClick={() => { setCart([{ ...selected, qty }]); navigateTo("checkout"); }}>
                    Buy Now <Icon name="arrowRight" size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CART VIEW */}
        {view === "cart" && (
          <div className="cart-page page-enter">
            <button className="back-btn" onClick={() => navigateTo("shop")}><Icon name="arrowLeft" size={13} /> Continue Shopping</button>
            <div className="section-title" style={{ marginBottom: 4 }}>Your Cart</div>
            <div className="section-sub" style={{ marginBottom: 20 }}>{cartCount} item{cartCount !== 1 ? "s" : ""} in your cart</div>
            {cart.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="cart" size={44} /></div>
                <h3>Your cart is empty</h3><p>Add some products to get started</p>
                <button className="btn btn-primary" onClick={() => navigateTo("shop")}>Browse Products</button>
              </div>
            ) : <>
              {cart.map(item => (
                <div className="cart-item" key={item.id}>
                  <div className="cart-item-icon" style={{ overflow: "hidden" }}>
                    {item.image ? <img src={item.image} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10, opacity: 1 }} /> : <CatIcon category={item.category} size={22} />}
                  </div>
                  <div className="cart-item-info">
                    <div className="ci-name">{item.name}</div>
                    <div className="ci-price">₹{item.price.toLocaleString()} per unit</div>
                  </div>
                  <div className="qty-btns">
                    <button onClick={() => updCartQty(item.id, -1)}><Icon name="minus" size={12} /></button>
                    <span style={{ fontSize: 13 }}>{item.qty}</span>
                    <button onClick={() => updCartQty(item.id, 1)}><Icon name="plus" size={12} /></button>
                  </div>
                  <div className="ci-total">₹{(item.price * item.qty).toLocaleString()}</div>
                  <button className="btn btn-danger btn-sm" onClick={() => removeFromCart(item.id)}><Icon name="x" size={12} /></button>
                </div>
              ))}
              <div className="cart-summary">
                <div className="summary-row"><span>Subtotal</span><span>₹{cartTotal.toLocaleString()}</span></div>
                <div className="summary-row"><span>Shipping</span><span className="shipping-free"><Icon name="truck" size={12} /> Free shipping</span></div>
                <div className="summary-row total"><span>Total</span><strong>₹{cartTotal.toLocaleString()}</strong></div>
              </div>
              <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 14, padding: "13px", fontSize: 14 }} onClick={() => navigateTo("checkout")}>
                Proceed to Checkout <Icon name="arrowRight" size={14} />
              </button>
            </>}
          </div>
        )}

        {/* CHECKOUT VIEW */}
        {view === "checkout" && (
          <div className="checkout-page page-enter">
            <button className="back-btn" onClick={() => navigateTo("cart")}><Icon name="arrowLeft" size={13} /> Back to Cart</button>
            <div className="section-title" style={{ marginBottom: 22 }}>Checkout</div>
            <div className="checkout-grid">
              <div>
                <div className="checkout-section">
                  <h3><Icon name="pin" size={15} /> Delivery Information</h3>
                  <div className="form-field"><label>Full Name <span style={{ color: "var(--red)" }}>*</span></label><input value={form.name} onChange={e => upd("name", e.target.value)} /></div>
                  <div className="form-field"><label>Street Address <span style={{ color: "var(--red)" }}>*</span></label><input value={form.address} onChange={e => upd("address", e.target.value)} placeholder="Building, Street, Area" /></div>
                  <div className="form-row">
                    <div className="form-field"><label>City <span style={{ color: "var(--red)" }}>*</span></label><input value={form.city} onChange={e => upd("city", e.target.value)} placeholder="Mumbai" /></div>
                    <div className="form-field"><label>State <span style={{ color: "var(--red)" }}>*</span></label><input value={form.state} onChange={e => upd("state", e.target.value)} placeholder="Maharashtra" /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-field"><label>PIN Code <span style={{ color: "var(--red)" }}>*</span></label><input value={form.pin} onChange={e => upd("pin", e.target.value)} placeholder="400001" /></div>
                    <div className="form-field"><label>Contact Number <span style={{ color: "var(--red)" }}>*</span></label><input value={form.contact} onChange={e => upd("contact", e.target.value)} placeholder="9876543210" /></div>
                  </div>
                  <div className="form-field"><label>Registration Number <span style={{ color: "var(--red)" }}>*</span></label><input value={form.regNumber} onChange={e => upd("regNumber", e.target.value)} placeholder="Registration Number" /></div>
                  <div className="form-field"><label>Notes (Optional)</label><textarea value={form.notes} onChange={e => upd("notes", e.target.value)} placeholder="Special delivery instructions…" /></div>
                </div>
                <div className="checkout-section">
                  <h3><Icon name="upi" size={15} /> Payment Method</h3>
                  <div className="payment-options">
                    {payMethods.map(pm => (
                      <div key={pm.id} className={`pay-opt ${payment === pm.id ? "selected" : ""}`} onClick={() => setPayment(pm.id)}>
                        <div className="pay-opt-radio"><div className="pay-dot" /></div>
                        <span className="pay-opt-icon"><Icon name={pm.icon} size={18} /></span>
                        <div><div className="pay-opt-label">{pm.label}</div><div className="pay-opt-desc">{pm.desc}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="order-summary-box">
                <h3>Order Summary</h3>
                {cart.map(i => (
                  <div className="summary-item" key={i.id}>
                    <div className="si-icon" style={{ overflow: "hidden" }}>
                      {i.image ? <img src={i.image} alt={i.name} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8, opacity: 1 }} /> : <CatIcon category={i.category} size={16} />}
                    </div>
                    <div><div className="si-name">{i.name}</div><div className="si-qty">Qty: {i.qty}</div></div>
                    <div className="si-price">₹{(i.price * i.qty).toLocaleString()}</div>
                  </div>
                ))}
                <div style={{ paddingTop: 14, borderTop: "1px solid var(--border)", marginTop: 8 }}>
                  <div className="summary-row" style={{ fontSize: 12 }}><span>Subtotal</span><span>₹{cartTotal.toLocaleString()}</span></div>
                  <div className="summary-row" style={{ fontSize: 12 }}><span>Shipping</span><span style={{ color: "var(--green)", fontWeight: 600 }}>Depends on your Order</span></div>
                  <div className="summary-row total"><span>Total</span><strong>₹{cartTotal.toLocaleString()}</strong></div>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%", justifyContent: "center", padding: "13px", marginTop: 16 }}
                  onClick={() => { if (form.address && form.city && form.state && form.pin && form.contact && form.regNumber) placeOrder(); else showToast("Please complete all required delivery details (incl. Reg. Number)"); }}
                >
                  Place Order <Icon name="check" size={14} />
                </button>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, fontSize: 11, color: "var(--text3)" }}>
                  <Icon name="lock" size={11} /> Secure &amp; encrypted checkout
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONFIRM VIEW */}
        {view === "confirm" && lastOrder && (
          <div className="confirm-page">
            <div className="confirm-success-badge">
               <div className="confirm-icon-glow">
                 <Icon name="check" size={32} />
               </div>
            </div>
            <h2>Order Confirmed</h2>
            <p className="conf-sub">Your order has been placed successfully.<br />You'll be notified once it ships.</p>
            
            <div className="order-info-card">
              <div className="oi-row"><span className="oir-key">Order ID</span><span className="oir-val" style={{color:"var(--blue)"}}>{lastOrder.id}</span></div>
              <div className="oi-row">
                <span className="oir-key">Items</span>
                <span className="oir-val">{lastOrder.items.map(i => `${i.name} ×${i.qty}`).join(", ")}</span>
              </div>
              <div className="oi-row"><span className="oir-key">Total Amount</span><span className="oir-val">₹{lastOrder.total.toLocaleString()}</span></div>
              <div className="oi-row"><span className="oir-key">Payment Method</span><span className="oir-val">{lastOrder.payment}</span></div>
              <div className="oi-row"><span className="oir-key">Reg. Number</span><span className="oir-val">{lastOrder.regNumber || "N/A"}</span></div>
              <div className="oi-row"><span className="oir-key">Delivery Address</span><span className="oir-val">{lastOrder.address}</span></div>
              <div className="oi-row"><span className="oir-key">Estimated Delivery</span><span className="oir-val" style={{color:"var(--green)"}}>3–5 Business Days</span></div>
            </div>

            <div className="confirm-actions">
              <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", padding: 14 }} onClick={() => { navigateTo("orders"); setLastOrder(null); }}>
                <Icon name="orders" size={14} /> View My Orders
              </button>
              <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", padding: 12, marginTop: 10 }} onClick={() => { navigateTo("shop"); setLastOrder(null); }}>
                Continue Shopping <Icon name="arrowRight" size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ORDERS VIEW */}
        {view === "orders" && (
          <div className="user-main">
            <div className="section-title" style={{ marginBottom: 4 }}>My Orders</div>
            <div className="section-sub" style={{ marginBottom: 22 }}>Track and view your order history</div>
            {userOrders.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="orders" size={44} /></div>
                <h3>No Orders Yet</h3><p>Place your first order to see it here</p>
                <button className="btn btn-primary" onClick={() => navigateTo("shop")}>Start Shopping</button>
              </div>
            ) : (
              [...userOrders].reverse().map(o => (
                <div className="order-card" key={o.id}>
                  <div className="order-card-header">
                    <div><div className="order-id">{o.id}</div><div className="order-date">{o.date}</div></div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="order-items">{o.items.map(i => `${i.name} ×${i.qty}`).join("  ·  ")}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span className="order-total">₹{o.total.toLocaleString()}</span>
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>{o.payment}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>{/* end user-content */}

      {toast && <Toast msg={toast} onClose={() => setToast("")} />}

      {showUpiModal && (
        <UpiPaymentModal
          amount={cartTotal}
          contact={form.contact}
          onClose={() => setShowUpiModal(false)}
          onVerifyAndPay={async (upiId, utr) => {
            setShowUpiModal(false);
            try {
              const orderData = {
                items: cart.map(i => ({ productId: i.id, name: i.name, qty: i.qty, price: i.price })),
                total: cartTotal,
                address: `${form.address}, ${form.city}, ${form.state} – ${form.pin}`,
                contact: form.contact, 
                payment: "UPI (Verified with UTR)", 
                notes: `UPI ID: ${upiId || "N/A"} | UTR Ref: ${utr} | ${form.notes}`, 
                regNumber: form.regNumber,
                utrNumber: utr
              };
              const order = await onPlaceOrder(orderData);
              setLastOrder(order);
              setCart([]);
              navigateTo("confirm");
              showToast("Order Placed Successfully! Your UTR has been recorded.");
            } catch (err) {
              showToast("Failed to place UPI order.");
            }
          }}
        />
      )}
    </div>
  );
}
